import { StatusBadge, type Tom } from "@/components/data/status-badge";
import { labelReferencial } from "@shared/schema";
import { fmtMoedaBR } from "@/lib/formato-mercado";

/**
 * O chip de estado do /mercado — um `StatusBadge` com ponto, nomeado pela
 * FAMÍLIA de estado em vez de pelo tom. As famílias vêm da spec do Mercado:
 *
 *   aberta  → neutro   (Enviado · A pagar · Sem acesso)
 *   vista   → info     (Visto)
 *   conf    → sucesso  (Conferido · Ajustado · Parcial — chega no C3)
 *   inf     → atenção  (Informado — chega no C4)
 *   pago    → sucesso  (Pago · Ativo)
 *   warn    → atenção  (vence esta semana · Convidado)
 *   danger  → erro     (vencido)
 *   info    → info
 *
 * Só as cinco cores do sistema entram aqui: `conf` era teal no protótipo, mas
 * teal não é token da plataforma e um sexto tom não se cria por um chip.
 */
export type StatusChipKind = "aberta" | "vista" | "conf" | "inf" | "pago" | "warn" | "danger" | "info";

const TOM: Record<StatusChipKind, Tom> = {
  aberta: "neutro",
  vista: "info",
  conf: "sucesso",
  inf: "atencao",
  pago: "sucesso",
  warn: "atencao",
  danger: "erro",
  info: "info",
};

export function StatusChip({
  kind, children, className, testId,
}: {
  kind: StatusChipKind;
  children: React.ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <StatusBadge tom={TOM[kind]} ponto className={className} testId={testId}>
      {children}
    </StatusBadge>
  );
}

/**
 * Chip de pagamento — o mesmo nas duas pontas do balcão.
 *
 * ⚠️ `INFORMADO` e `PARCIAL_INFORMADO` continuam aqui, e não são resto: o
 * pagamento do ROMANEIO passou a quitar no ato ("quem registra quita"), mas o
 * RECEBIMENTO da carga (C8b, corretor ↔ distribuidor) ainda tem as duas pontas,
 * e é o mesmo chip que serve os dois. Azul e não verde: alguém AFIRMA ter pago e
 * ninguém confirmou, e pintá-lo de verde faria quem recebe achar que caiu.
 *
 * `CONTESTADO` em vermelho porque é a única linha que pede conversa agora. No
 * romaneio ele só aparece como histórico — ninguém contesta mais.
 */
export function ChipPagamento({
  status, rotuloAberto = "A pagar", excedenteValor,
}: {
  status: string;
  rotuloAberto?: string;
  /**
   * Quanto passou da dívida, quando passou.
   *
   * ⚠️ "Pago" e "Pago · R$ 21,00 a mais" são fatos DIFERENTES, e a diferença é
   * dinheiro de alguém. O excedente virou PAGO (a dívida está quitada) justamente
   * para sair da fila de espera onde ficava preso — e é este rótulo que impede que
   * sair da fila signifique desaparecer.
   */
  excedenteValor?: number;
}) {
  if (status === "PAGO") {
    return excedenteValor && excedenteValor > 0
      ? <StatusChip kind="warn">Pago · {fmtMoedaBR(excedenteValor)} a mais</StatusChip>
      : <StatusChip kind="pago">Pago</StatusChip>;
  }
  if (status === "CONTESTADO") return <StatusChip kind="danger">Contestado</StatusChip>;
  if (status === "INFORMADO") return <StatusChip kind="inf">Informado</StatusChip>;
  if (status === "PARCIAL_INFORMADO") return <StatusChip kind="inf">Parcial · informado</StatusChip>;
  if (status === "PARCIAL") return <StatusChip kind="warn">Parcial</StatusChip>;
  // ⚠️ O aberto é o ÚNICO rótulo que muda de lado: a mesma máquina de estados
  // serve "A pagar" (C4) e "A receber" (C8b), e `A_RECEBER` chega aqui pelo
  // mesmo caminho que `A_PAGAR`. Um segundo componente duplicaria os cinco
  // estados de cima só para trocar duas palavras.
  return <StatusChip kind="aberta">{rotuloAberto}</StatusChip>;
}

/**
 * O ESTADO DA CARGA do comprador (C5a) — outra coisa de `ChipCarga`, que é o
 * caminho da NOTA do produtor até o comprador.
 *
 * ⚠️ Três degraus, e nenhum deles fala de dinheiro: pagamento e recebimento são
 * eixo SEPARADO (`status_recebimento`, PR D). "Entregue" com dinheiro em aberto
 * é o estado mais comum de todos, e um chip só teria que dizer as duas coisas.
 */
export function ChipEstadoCarga({ status }: { status: string }) {
  if (status === "ENTREGUE") return <StatusChip kind="pago">Entregue</StatusChip>;
  if (status === "FECHADA") return <StatusChip kind="vista">Fechada</StatusChip>;
  return <StatusChip kind="warn">Em formação</StatusChip>;
}

/**
 * A NATUREZA do item da carga: romaneio do Rizoma (verde) ou registro próprio
 * do comprador (âmbar).
 *
 * ⚠️ Âmbar não é "erro nem pendência": registro próprio é um item legítimo e
 * completo, e é ele que faz a carga fechar. O âmbar diz "este número é seu, e
 * ninguém do outro lado confirmou ainda" — o que muda depois que o produtor
 * confere pelo link (PR B) ou cria conta e formaliza.
 */
export function ChipNaturezaItem({ noRizoma }: { noRizoma: boolean }) {
  return noRizoma
    ? <StatusChip kind="pago">no Rizoma</StatusChip>
    : <StatusChip kind="warn">seu registro</StatusChip>;
}

/**
 * "Divulgado como" — a etiqueta da cotação (059).
 *
 * ⚠️ O chip existe porque os dois números NÃO são comparáveis e nada os
 * converte: uma placa "ao produtor" é o que o produtor recebe; uma placa de
 * venda é o preço da caixa embalada na mão do comprador. Mostrar preço de praça
 * sem esta etiqueta é convidar a leitura errada — e é ela que decide a FORMA da
 * comparação (% quando as bases batem, R$/kg de diferença quando não batem).
 */
export function ChipReferencial({ referencial, testId }: { referencial: string; testId?: string }) {
  return (
    <StatusChip kind={referencial === "VENDA" ? "info" : "pago"} testId={testId}>
      {labelReferencial(referencial)}
    </StatusChip>
  );
}
